
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MapPin, Users, BookOpen, Target, Heart, Globe, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { stats } from "@/api/apiService";
import { CACHE_DURATION, STATS_CACHE_KEY } from "@/api/apiConfig";

const StatSkeleton = () => (
  <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background shadow-sm">
    <div
      className="w-12 h-12 rounded-full mb-4"
      style={{
        background: 'linear-gradient(90deg, hsl(var(--muted)) 25%, hsl(var(--muted-foreground) / 0.2) 50%, hsl(var(--muted)) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s ease-in-out infinite'
      }}
    />
    <div
      className="h-8 w-16 rounded mb-2"
      style={{
        background: 'linear-gradient(90deg, hsl(var(--muted)) 25%, hsl(var(--muted-foreground) / 0.2) 50%, hsl(var(--muted)) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s ease-in-out infinite'
      }}
    />
    <div
      className="h-6 w-32 rounded mb-1"
      style={{
        background: 'linear-gradient(90deg, hsl(var(--muted)) 25%, hsl(var(--muted-foreground) / 0.2) 50%, hsl(var(--muted)) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s ease-in-out infinite'
      }}
    />
    <div
      className="h-4 w-24 rounded"
      style={{
        background: 'linear-gradient(90deg, hsl(var(--muted)) 25%, hsl(var(--muted-foreground) / 0.2) 50%, hsl(var(--muted)) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s ease-in-out infinite'
      }}
    />
  </div>
);

const About = () => {
  const [statistics, setStatistics] = useState(null);

  const getCachedStats = () => {
    try {
      const cached = localStorage.getItem(STATS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
        // Cache expired, remove it
        localStorage.removeItem(STATS_CACHE_KEY);
      }
    } catch (error) {
      console.error('Error reading cached stats:', error);
      localStorage.removeItem(STATS_CACHE_KEY);
    }
    return null;
  };

  const setCachedStats = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching stats:', error);
    }
  };

  useEffect(() => {
    // First, try to get cached stats
    const cachedStats = getCachedStats();
    if (cachedStats) {
      setStatistics(cachedStats);
      return;
    }

    // If no cached stats or cache expired, fetch from API
    stats().then(res => {
      try {
        if (res) {
          setStatistics(res);
          setCachedStats(res);
        }
      } catch (error) {
        console.log(error);
      }
    });
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 mt-16">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-8">About Us</h1>
          <div className="max-w-3xl mx-auto space-y-6 text-lg text-muted-foreground">
            <p>
              Welcome to Ulixes App, where we combine cutting-edge technology with the joy of exploration.
              Our mission is to revolutionize travel experiences by providing AI-generated city guides tailored
              to your interests. Whether you're a seasoned traveler or embarking on your first adventure,
              our guides offer curated recommendations and insights to enrich your journey.
            </p>
            <p>
              At Ulixes, we are driven by our passion for discovery and our commitment to making travel
              accessible to everyone. With over 1000 cities covered and 50000 happy users worldwide, we strive
              to deliver guides that are informative, reliable, and inspiring.
            </p>
            <p>
              Join us in exploring the world's wonders effortlessly, guided by the power of AI and the
              expertise of our dedicated team.
            </p>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="bg-primary/5 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Impact</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Numbers that reflect our commitment to revolutionizing travel experiences worldwide
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {statistics ? (
                <>
                  <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background shadow-sm">
                    <MapPin className="w-12 h-12 text-primary mb-4" />
                    <h3 className="text-3xl font-bold mb-2">{statistics.unique_cities}</h3>
                    <p className="text-lg font-medium">Locations Explored</p>
                    <p className="text-muted-foreground">in {statistics.unique_countries} Countries</p>
                  </div>

                  <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background shadow-sm">
                    <Users className="w-12 h-12 text-primary mb-4" />
                    <h3 className="text-3xl font-bold mb-2">{statistics.unique_users}</h3>
                    <p className="text-lg font-medium">Happy Travelers</p>
                    <p className="text-muted-foreground">loved by explorers like you</p>
                  </div>

                  <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background shadow-sm">
                    <BookOpen className="w-12 h-12 text-primary mb-4" />
                    <h3 className="text-3xl font-bold mb-2">{statistics.total_guides}</h3>
                    <p className="text-lg font-medium">Guides Created</p>
                    <p className="text-muted-foreground">every guide, a story to follow</p>
                  </div>
                </>
              ) : (
                <>
                  <StatSkeleton />
                  <StatSkeleton />
                  <StatSkeleton />
                </>
              )}
            </div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    To democratize travel planning by making personalized, AI-powered city guides
                    accessible to every traveler, regardless of their experience level or budget.
                  </p>
                  <p>
                    We believe that every journey should be unique, memorable, and tailored to
                    individual preferences. Our technology transforms the way people discover
                    and explore the world.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-6 rounded-lg bg-primary/5">
                  <Target className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Precision</h3>
                  <p className="text-sm text-muted-foreground">AI-powered recommendations tailored to you</p>
                </div>
                <div className="text-center p-6 rounded-lg bg-primary/5">
                  <Heart className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Passion</h3>
                  <p className="text-sm text-muted-foreground">Built by travelers, for travelers</p>
                </div>
                <div className="text-center p-6 rounded-lg bg-primary/5">
                  <Globe className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Global</h3>
                  <p className="text-sm text-muted-foreground">Covering cities worldwide</p>
                </div>
                <div className="text-center p-6 rounded-lg bg-primary/5">
                  <Zap className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Innovation</h3>
                  <p className="text-sm text-muted-foreground">Cutting-edge AI technology</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How We Work Section */}
        <section className="bg-muted/50 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How We Work</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our advanced AI algorithms analyze your preferences to create personalized travel experiences
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Tell Us Your Preferences</h3>
                <p className="text-muted-foreground">
                  Share your interests, budget, travel style, and preferences to help our AI understand what you're looking for.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">AI Creates Your Guide</h3>
                <p className="text-muted-foreground">
                  Our advanced algorithms analyze thousands of data points to craft a personalized itinerary just for you.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Explore & Enjoy</h3>
                <p className="text-muted-foreground">
                  Follow your custom guide with interactive maps, local insights, and real-time recommendations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get in Touch</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Have questions or feedback? We'd love to hear from you.
            </p>
            <div className="inline-flex items-center gap-2 text-primary">
              <span className="text-lg font-medium">support@ulixes.app</span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
